import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  Button,
  Modal,
  Input,
  Form,
  Space,
  Typography,
  message,
} from 'antd';
import ActiveStatusFormItem from 'app/shared/components/Form/ActiveStatusFormItem';
import ProductStatusFilter from 'app/shared/components/ProductStatusFilter';
import type { ProductStatusFilterValue } from 'app/shared/components/productStatusFilterUtils';
import { matchesProductStatusFilter } from 'app/shared/components/productStatusFilterUtils';
import ManagerSearchByName from 'app/shared/components/ManagerSearchByName';
import { PlusOutlined } from '@ant-design/icons';

import {
  CourseLanguage,
  fetchCourseLanguages,
  fetchCourseLanguageById,
  saveCourseLanguage,
  openModal,
  closeModal,
  setFilter,
} from './actions';

import {
  getLanguageLoading,
  getLanguageSaving,
  getFilterName,
  getModalOpen,
  getEditingItem,
  getLanguageList,
} from './selectors';

import { PublishedEventType, StatusIcon, useSignalREvent } from 'app/helpers/signalRHelper';

import './Page.css';

const { Title } = Typography;

const CourseLanguageManagerPage: React.FC = () => {
  const dispatch = useDispatch<any>();
  const loading = useSelector(getLanguageLoading);
  const saving = useSelector(getLanguageSaving);
  const list = useSelector(getLanguageList);
  const filterName = useSelector(getFilterName);
  const [activeStatusFilter, setActiveStatusFilter] = useState<ProductStatusFilterValue>('all');
  const modalOpen = useSelector(getModalOpen);
  const editing = useSelector(getEditingItem);
  const [form] = Form.useForm();

  const refreshGridEvent = async () => {
    dispatch(fetchCourseLanguages());
  };

  // Subscribe to event and run refreshGridEvent when triggered
  const { status, lastEventReceived } = useSignalREvent(
    [PublishedEventType.CourseLanguageEventPublished],
    refreshGridEvent
  );

  useEffect(() => {
    dispatch(fetchCourseLanguages());
  }, [dispatch]);

  const handleAdd = () => {
    dispatch(openModal(null));
    form.resetFields();
    form.setFieldsValue({ name: '', active: true });
  };

  const handleOpenEdit = async (record: CourseLanguage) => {
    dispatch(openModal(record));
    form.resetFields();
    const fresh = await dispatch(fetchCourseLanguageById(record.id));
    form.setFieldsValue({
      name: fresh?.name ?? record.name,
      active: typeof fresh?.active === 'boolean' ? fresh.active : record.active,
    });
  };

  const uniqueNameValidator = async (_: any, value?: string) => {
    const name = (value ?? '').trim().toLowerCase();
    if (!name) return Promise.resolve();
    const currentId = editing?.id;
    const exists = list.some(
      (c) => c.name.trim().toLowerCase() === name && c.id !== currentId
    );
    if (exists) return Promise.reject(new Error('Language name must be unique.'));
    return Promise.resolve();
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        id: editing?.id,
        name: values.name.trim(),
        active: !!values.active,
      };
      const res = await dispatch(saveCourseLanguage(payload));
      if (res.ok) {
        message.success(
          editing ? 'Language updated successfully.' : 'Language created successfully.'
        );
        dispatch(closeModal());
        form.resetFields();
      } else {
        message.error(res.message || 'Failed to save language.');
      }
    } catch (err: any) {
      if (!err?.errorFields) {
        message.error(err?.message || 'Failed to save language.');
      }
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 40,
      render: (id: number) => <span>{id || '-'}</span>,
      sorter: (a: CourseLanguage, b: CourseLanguage) => a.id - b.id,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      width: 200,
      key: 'name',
      render: (_: any, record: CourseLanguage) => (
        <Button type="link" style={{ padding: 0 }} onClick={() => handleOpenEdit(record)}>
          {record.name}
        </Button>
      ),
      sorter: (a: CourseLanguage, b: CourseLanguage) => a.name.localeCompare(b.name),
    },
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      key: 'createdBy',
      width: 120,
      render: (text: string) => <span>{text || '-'}</span>,
    },
    {
      title: 'Last Modified By',
      dataIndex: 'lastModifiedBy',
      key: 'lastModifiedBy',
      width: 120,
      render: (text: string) => <span>{text || '-'}</span>,
    },
    {
      title: 'Active',
      dataIndex: 'active',
      key: 'active',
      align: 'center' as const,
      width: 40,
      render: (val: boolean) =>
        val ? (
          <span style={{ color: 'green' }}>Yes</span>
        ) : (
          <span style={{ color: 'red' }}>No</span>
        ),
    },
  ];

  const displayed = useMemo(() => {
    const needle = (filterName || '').trim().toLowerCase();
    let data = Array.isArray(list) ? [...list] : [];

    data = data.filter((item) => matchesProductStatusFilter(item.active, activeStatusFilter));
    if (needle) data = data.filter((x) => x.name?.toLowerCase().includes(needle));

    return data;
  }, [list, filterName, activeStatusFilter]);

  return (
    <>
      <Title level={3} style={{ margin: 0, marginBottom: 16 }}>Course Language Manager</Title>

      <Space
        style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', width: '100%' }}
      >
        <Space>
          <ManagerSearchByName value={filterName} onChange={(v) => dispatch(setFilter(v))} />
          <ProductStatusFilter
            value={activeStatusFilter}
            onChange={setActiveStatusFilter}
          />
        </Space>

        <Space>
          <StatusIcon
            status={status}
            lastEventReceived={lastEventReceived}
            unpublishedChanges={false}
            onManualRefresh={refreshGridEvent}
            style={{ fontSize: 14, fontWeight: 'bold' }}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Add New
          </Button>
        </Space>
      </Space>

      <Table<CourseLanguage>
        rowKey="id"
        loading={loading}
        dataSource={displayed}
        columns={columns}
        pagination={{ pageSize: 10, showSizeChanger: false }}
        rowClassName={(record) => (!record.active ? 'inactive-row' : '')}
      />

      <Modal
        open={modalOpen}
        title={editing ? 'Edit Language' : 'Add Language'}
        onCancel={() => {
          dispatch(closeModal());
          form.resetFields();
        }}
        onOk={handleSave}
        okText="OK"
        confirmLoading={saving}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            label="Name"
            name="name"
            rules={[
              { required: true, message: 'Please enter a language name' },
              { validator: uniqueNameValidator },
            ]}
          >
            <Input maxLength={60} placeholder="Enter unique language name" />
          </Form.Item>
          <ActiveStatusFormItem name="active" initialValue={true} />
        </Form>
      </Modal>
    </>
  );
};

export default CourseLanguageManagerPage;